import React, { useMemo } from 'react';
import { money, SortableTh, useTableSort } from '../weeklyTableHelpers';
import { KM_BANDS, LEGACY_COUNT_COLUMNS, LEGACY_FEE_COLUMNS } from './invoicingSurface';

/**
 * The legacy per-trip invoicing sub-table: one row per vehicle class over the nine KM bands,
 * plus the call-out quantity and the two fee columns.
 *
 * Moved out of InvoicingTable.jsx unchanged when the two per-seat tables were added — the
 * body below is the original verbatim, so the 28 companies on a split model see exactly what
 * they saw before. Only the component name and the two renamed column constants differ.
 *
 * @param {Object} props
 * @param {Object} props.toggleTable - The company's `toggle_table` payload.
 * @param {number|string} props.companyId - Used only for the sub-table testid.
 * @returns {JSX.Element}
 */
const LegacyPerTripTable = ({ toggleTable, companyId }) => {
  const vehicleRows = useMemo(
    () => [toggleTable.sedan, toggleTable.van, toggleTable.minibus, toggleTable.hatchback].filter(Boolean),
    [toggleTable]
  );

  // Vehicle rows are flat, so sort by direct property access rather than dot-path.
  const { sortConfig, handleSort, sortedRows } = useTableSort(vehicleRows, { getValue: (row, key) => row[key] });

  return (
    <table className="table-fixed w-full border-collapse" data-testid={`invoicing-subtable-${companyId}`}>
      <thead>
        <tr>
          <SortableTh label="Vehicle" sortKey="vehicle" sortConfig={sortConfig} onSort={handleSort} testId={`invoicing-subth-vehicle-${companyId}`} />
          {KM_BANDS.map((band) => (
            <SortableTh
              key={band.key}
              label={band.label}
              sortKey={band.key}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="center"
              testId={`invoicing-subth-${band.key}-${companyId}`}
            />
          ))}
          {[...LEGACY_COUNT_COLUMNS, ...LEGACY_FEE_COLUMNS].map((column) => (
            <SortableTh
              key={column.key}
              label={column.label}
              sortKey={column.key}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="center"
              testId={`invoicing-subth-${column.key}-${companyId}`}
            />
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((vehicle) => (
          <tr key={vehicle.vehicle}>
            <td className="text-gray-700 font-bold border-t border-b p-2 text-left">{vehicle.vehicle}</td>
            {KM_BANDS.map((band) => (
              <td key={band.key} className="text-gray-700 border-t border-b p-2 text-center">
                {vehicle[band.key] ?? 0}
              </td>
            ))}
            {LEGACY_COUNT_COLUMNS.map((column) => (
              <td
                key={column.key}
                className="text-gray-700 border-t border-b p-2 text-center"
                data-testid={`invoicing-subtd-${column.key}-${companyId}`}
              >
                {vehicle[column.key] ?? 0}
              </td>
            ))}
            {LEGACY_FEE_COLUMNS.map((fee) => (
              <td
                key={fee.key}
                className="text-gray-700 border-t border-b p-2 text-center"
                data-testid={`invoicing-subtd-${fee.key}-${companyId}`}
              >
                {money(vehicle[fee.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default LegacyPerTripTable;
